import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// Presigned download URLs expire after 15 min on the backend; refresh a bit earlier.
const DOWNLOAD_URL_TTL_MS = 13 * 60 * 1000;
const downloadUrlCache = new Map(); // key -> { url, expiresAt } | { promise }

const apiUrl = (path) => `${API_URL.replace(/\/+$/, "")}/${path}`;

// Values saved before the presigned flow are full URLs (or local assets); new ones are S3 keys.
export const isS3Key = (value) =>
  typeof value === "string" &&
  value.length > 0 &&
  !/^(https?:|blob:|data:|\/)/i.test(value);

/**
 * Upload a file through the backend presigned URL flow.
 * Returns the S3 key to store in the DB.
 */
export const uploadToS3 = async (file, accessToken) => {
  const { data } = await axios.post(
    apiUrl("s3/upload-url"),
    { filename: file.name, contentType: file.type || "application/octet-stream" },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  // Plain fetch: the presigned URL must not receive our Authorization header.
  const res = await fetch(data.url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) throw new Error(`S3 upload failed with status ${res.status}`);

  return data.key;
};

/**
 * Turn a stored value into something an <img>/<a> can use.
 * Full URLs pass through untouched; S3 keys get a (cached) presigned download URL.
 */
export const resolveS3Url = async (value, accessToken) => {
  if (!isS3Key(value)) return value;

  const cached = downloadUrlCache.get(value);
  if (cached?.promise) return cached.promise;
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  const promise = axios
    .get(apiUrl("s3/download-url"), {
      params: { key: value },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    .then(({ data }) => {
      downloadUrlCache.set(value, { url: data.url, expiresAt: Date.now() + DOWNLOAD_URL_TTL_MS });
      return data.url;
    })
    .catch((error) => {
      downloadUrlCache.delete(value);
      throw error;
    });

  downloadUrlCache.set(value, { promise });
  return promise;
};

// Display name for an uploaded file: strips the "<uuid>-" (new) or "<uuid>_" (old) prefix.
export const s3FileName = (value) => {
  if (!value || typeof value !== "string") return "";
  const base = decodeURIComponent(value.split("?")[0]).split("/").pop();
  return base.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[-_]/i, "");
};
